import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  Stack,
  Button,
} from '@mui/material';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import AiCardBase from './AiCardBase';

const ConditionsAiCard = ({ card, onConfirm, isConfirmed, isConfirming }) => {
  const { cardType, label, data } = card;

  // Normalize data into array of conditions
  const extractConditions = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw.conditions)) return raw.conditions;
    if (raw.patientConditionId) return [raw];
    return [];
  };

  const conditionList = extractConditions(data);

  // Default to selecting all extracted conditions
  const [selectedIds, setSelectedIds] = useState(() =>
    conditionList.map((c) => c.patientConditionId || c.id).filter(Boolean)
  );

  useEffect(() => {
    const list = extractConditions(data);
    setSelectedIds(list.map((c) => c.patientConditionId || c.id).filter(Boolean));
  }, [data]);

  const handleToggle = (id) => {
    if (isConfirmed) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (isConfirmed) return;
    const all = conditionList.map((c) => c.patientConditionId || c.id).filter(Boolean);
    setSelectedIds(all);
  };

  const handleDeselectAll = () => {
    if (isConfirmed) return;
    setSelectedIds([]);
  };

  const handleConfirm = () => {
    onConfirm('conditions', { patientConditionIds: selectedIds });
  };

  return (
    <AiCardBase
      cardType={cardType}
      label={label || 'Patient Conditions'}
      isConfirmed={isConfirmed}
      isConfirming={isConfirming}
      onConfirm={handleConfirm}
      isUpdatedByAi={card.isUpdatedByAi}
    >
      <Box sx={{ py: 1 }}>
        <Alert severity="info" sx={{ mb: 2, fontSize: 13, py: 0.5 }}>
          Conditions are staged directly in patient records with unconfirmed status. Select the conditions you wish to confirm.
        </Alert>

        {!isConfirmed && conditionList.length > 1 && (
          <Stack direction="row" spacing={1} mb={1.5}>
            <Button size="small" variant="text" onClick={handleSelectAll} sx={{ fontSize: 12 }}>
              Select all
            </Button>
            <Button size="small" variant="text" onClick={handleDeselectAll} sx={{ fontSize: 12 }}>
              Clear all
            </Button>
          </Stack>
        )}

        <List disablePadding>
          {conditionList.map((condition, index) => {
            const id = condition.patientConditionId || condition.id || `cond-${index}`;
            const name = condition.conditionName || condition.name || 'Medical Condition';
            const isChecked = selectedIds.includes(id);

            return (
              <ListItem
                key={id}
                disableGutters
                sx={{
                  p: 1.25,
                  mb: 1,
                  borderRadius: 2,
                  bgcolor: isChecked ? 'rgba(138, 75, 255, 0.05)' : '#F8FAFC',
                  border: isChecked ? '1px solid rgba(138, 75, 255, 0.3)' : '1px solid #E2E8F0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Checkbox
                    checked={isChecked}
                    disabled={isConfirmed}
                    onChange={() => handleToggle(id)}
                    color="primary"
                    data-testid={`checkbox-condition-${id}`}
                  />
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: 14 }}>
                      {name}
                    </Typography>
                    {condition.notes && (
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        {condition.notes}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Chip
                  label={isChecked ? (isConfirmed ? 'Confirmed' : 'Selected') : 'Excluded'}
                  size="small"
                  sx={{
                    bgcolor: isChecked
                      ? isConfirmed
                        ? '#ECFDF5'
                        : '#EDE9FE'
                      : '#F1F5F9',
                    color: isChecked
                      ? isConfirmed
                        ? '#059669'
                        : '#6D28D9'
                      : '#64748B',
                    fontWeight: 600,
                    fontSize: 11,
                  }}
                />
              </ListItem>
            );
          })}
        </List>

        {conditionList.length === 0 && (
          <Typography variant="body2" sx={{ color: '#94A3B8', fontStyle: 'italic', p: 1 }}>
            No condition entries found in this consultation.
          </Typography>
        )}
      </Box>
    </AiCardBase>
  );
};

export default ConditionsAiCard;
